// src/components/SettingsModal.tsx
import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import {
  Button,
  Dialog,
  DialogContent,
  Slide,
  Grid,
  Divider,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import apiCall from "../utils/apiCall";
import { UPDATE_USER_SETTINGS } from "../endpoint";

import { useAppDispatch, useAppSelector } from "../hooks/reduxhook";
import {
  addUserDetails,
  getuser,
  getChatHistory,
  modifyChatHistory,
} from "../redux/slices/userSlice";
import { getModelDetails } from "../redux/slices/modelSlice";
import TemperatureSlider from "./Slider";
import SettingRow from "./SettingRow";
import Loader from "./Loader";
import { addError } from "../redux/slices/errorSlice";
import { Close } from "@mui/icons-material";
import { StyledTooltip } from "./StyledTooltip";
import ChatHistory from "./ChatSection/ChatHistory";
import DeleteAllConversationsButton from "./LeftSidebar/DeleteAllConversationsButton";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ISettingModal {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SettingsModal: React.FC<ISettingModal> = ({ open, setOpen }) => {
  const { accounts } = useMsal();
  const accountId = accounts[0]?.username;
  const user = useAppSelector(getuser);
  const chatHistoryFromStore = useAppSelector(getChatHistory);

  // keep current (editable) values
  const [tempvalue, setTempvalue] = useState<number>(user?.temp ?? 0);
  const [chunk_size, setChunk_size] = useState<number>(user?.chunk_size ?? 1);

  // LIFTED: chatHistory controlled by modal now
  const [chatHistory, setChatHistory] = useState<number>(
    typeof chatHistoryFromStore === "number" ? chatHistoryFromStore : 0
  );

  // keep the "initial" values to detect changes and for reset
  const [initialTemp, setInitialTemp] = useState<number>(user?.temp ?? 0);
  const [initialChunk, setInitialChunk] = useState<number>(
    user?.chunk_size ?? 1
  );
  const [initialChatHistory, setInitialChatHistory] = useState<number>(
    typeof chatHistoryFromStore === "number" ? chatHistoryFromStore : 0
  );

  const [loading, setLoading] = useState(false);
  const model = useAppSelector(getModelDetails);

  // when modal opens (or user changes), reset both current and initial values
  useEffect(() => {
    if (open) {
      const t = user?.temp ?? 0;
      const c = user?.chunk_size ?? 1;
      const h =
        typeof chatHistoryFromStore === "number" ? chatHistoryFromStore : 0;

      setTempvalue(t);
      setChunk_size(c);
      setChatHistory(h);

      setInitialTemp(t);
      setInitialChunk(c);
      setInitialChatHistory(h);
    }
  }, [open, user?.temp, user?.chunk_size, chatHistoryFromStore]);

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setOpen(false);
  };

  const updateSettings = () => {
    setLoading(true);

    // Update Redux chatHistory now (we lifted the state into modal)
    dispatch(modifyChatHistory(chatHistory));

    apiCall("POST", UPDATE_USER_SETTINGS, {
      temp: tempvalue,
      chunk_size: chunk_size,
      model: model?.model,
      user_email: accountId,
      chat_history: chatHistory,
    })
      .then((res) => {
        // update redux store for user settings
        dispatch(addUserDetails(res?.result));

        // determine saved values (backend may normalize)
        const savedTemp = res?.result?.temp ?? tempvalue;
        const savedChunk = res?.result?.chunk_size ?? chunk_size;

        // reset initial values to reflect saved state so buttons become disabled again
        setInitialTemp(savedTemp);
        setInitialChunk(savedChunk);
        setInitialChatHistory(chatHistory);

        // also update current values to saved ones
        setTempvalue(savedTemp);
        setChunk_size(savedChunk);

        handleClose();
      })
      .catch((err) => {
        dispatch(addError("Failed to update the settings. Please try again."));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Reset handler: restore current values to the initial values captured when modal opened
  const handleReset = () => {
    setTempvalue(initialTemp);
    setChunk_size(initialChunk);
    setChatHistory(initialChatHistory);
  };

  // shared slider column style so all sliders match
  const sliderColSx = {
    flexBasis: { xs: "200px", sm: "280px" },
    flexGrow: 0,
    flexShrink: 0,
  };

  const numberLabelSx = { minWidth: "40px", textAlign: "right" as const };

  // isDirty when any value differs from its initial value
  const isDirty =
    tempvalue !== initialTemp ||
    chunk_size !== initialChunk ||
    chatHistory !== initialChatHistory;

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
      fullWidth
      maxWidth={"sm"}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 2,
          top: 2,
        }}
      >
        <Close sx={{ color: "gray" }} fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 4 }}>
        {user?.user_name !== "" && (
          <>
            <Divider sx={{ mb: 4 }}>
              <Typography fontWeight="bolder" sx={{ fontSize: "18px" }}>
                User Settings
              </Typography>
            </Divider>

            {/* Temperature */}
            <SettingRow
              title={
                <StyledTooltip
                  title="Use temperature to control randomness of text. Higher = more creative, lower = more focused."
                  placement="left"
                  arrow
                >
                  <Typography component="span" sx={{ fontWeight: "normal" }}>
                    Temperature
                  </Typography>
                </StyledTooltip>
              }
            >
              <Grid container alignItems="center" spacing={1}>
                <Grid item sx={sliderColSx}>
                  <TemperatureSlider
                    testid="TemperatureSlider"
                    setValue={(v: number) => setTempvalue(v)}
                    default_value={0}
                    max={1.0}
                    min={0}
                    step={0.1}
                    value={tempvalue}
                  />
                </Grid>

                <Grid item>
                  <Typography sx={numberLabelSx}>{tempvalue}</Typography>
                </Grid>
              </Grid>
            </SettingRow>

            {/* Top Ranked Pages */}
            <SettingRow
              title="Top Ranked Pages"
              tooltip="Set the number of top-ranked pages to consider when generating responses. A higher number may provide more context but could increase response time."
            >
              <Grid container alignItems="center" spacing={1}>
                <Grid item sx={sliderColSx}>
                  <TemperatureSlider
                    testid="RankedSlider"
                    setValue={(v: number) => setChunk_size(v)}
                    default_value={chunk_size}
                    max={50}
                    min={1}
                    step={1}
                    value={chunk_size}
                  />
                </Grid>

                <Grid item>
                  <Typography sx={numberLabelSx}>{chunk_size}</Typography>
                </Grid>
              </Grid>
            </SettingRow>

            {/* Chat History (lifted state: controlled by modal) */}
            <ChatHistory
              value={chatHistory}
              onChange={(v: number) => setChatHistory(v)}
            />

            {/* Delete All Conversations */}
            <SettingRow
              title={
                <StyledTooltip
                  title="Delete all your conversations permanently. This action cannot be undone."
                  placement="bottom-end"
                  arrow
                >
                  <Typography
                    sx={{
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "unset",
                    }}
                  >
                    Delete All Conversations
                  </Typography>
                </StyledTooltip>
              }
            >
              <Grid container alignItems="center">
                <Grid item sx={sliderColSx}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <DeleteAllConversationsButton />
                  </Box>
                </Grid>
              </Grid>
            </SettingRow>

            <Divider sx={{ mb: 4 }} />

            {/* Loader */}
            {loading && <Loader />}

            {/* Action buttons: Reset + Update */}
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}
            >
              <Button
                variant="contained"
                onClick={handleReset}
                color="primary"
                disabled={!isDirty || loading}
                data-testid="settings-reset-button"
              >
                Reset
              </Button>

              <Button
                variant="contained"
                onClick={updateSettings}
                color="primary"
                disabled={!isDirty || loading}
                data-testid="settings-button"
              >
                Update
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
